import Fetchers from 'core/fetchers';
import Utilities from 'shared/helpers/ProductHelper/utilities';
import ApplicationController from '../application_controller';

export default class ConfiguratorController extends ApplicationController {
  static outlets = ['navigation--cart', 'containers--drawer-component'];

  static values = {
    options:          Object,
    designId:         Number,
    productId:        Number,
    productImagesUrl: String,
  };

  static targets = [
    'optionInput', 'mockupSlide', 'previewSlide', 'mockupThumb', 'previewThumb', 'colorLabel',
    'addToCartButton', 'priceSale', 'priceRegular', 'colorSwatch', 'quantityInput',
  ];

  connect() {
    this.updateAvailableOptions();
    this.loadImageGroups();
  }

  // Same async endpoint (Design::ProductImages, via ProductPagesController#product_images)
  // the PDP itself fetches its gallery images from — rather than duplicating that
  // image-building logic server-side just for this drawer.
  loadImageGroups() {
    Fetchers.fetchJSON(this.productImagesUrlValue, 'GET', null)
      .then(res => res.json())
      .then((data) => { this.imageGroups = data.images; })
      .catch(() => { this.imageGroups = []; });
  }

  findImageGroup(productId) {
    const group = (this.imageGroups || []).find(g => g.product_ids.includes(productId));
    return group ? group.images : null;
  }

  // Our containers--drawer-component OUTLET points at .jsCartTray (used to open the cart
  // tray on success) — a different drawer instance from the one THIS configurator lives
  // inside. To close our own drawer, walk up to its controller element directly; it stows
  // its own controller instance on connect (see DrawerComponent#connect).
  get ownDrawer() {
    return this.element.closest('[data-controller~="containers--drawer-component"]')?.drawerComponent;
  }

  // The tile's "Select Options" trigger button lives outside this controller's element
  // Mirrors the PDP's { DesignOptions, CanvasOptions } nesting so Utilities.*
  // (productAttributes/productPrices/sanitizeProducts) work unmodified.
  get productOptionsShape() {
    return {
      DesignOptions: { active_product_ids: this.optionsValue.active_product_ids },
      CanvasOptions: { products: this.optionsValue.products },
    };
  }

  get sanitizedProducts() {
    // cache: false — never touch window.TeePublic.ProductOptions. This page can render
    // multiple tiles/drawers at once; sharing that global would leak state between them
    // (and between a drawer and a backgrounded PDP).
    if (!this.sanitizedProductsCache) {
      this.sanitizedProductsCache = Utilities.sanitizeProducts(this.productOptionsShape, false);
    }
    return this.sanitizedProductsCache;
  }

  optionChanged(event) {
    const selections = this.collectSelections();
    const { colors } = this.optionsValue;

    const resolved = this.findProduct(selections);
    if (resolved) {
      this.productIdValue = resolved.id;
      this.updatePrice(resolved);

      const imageSet = this.findImageGroup(resolved.id);
      if (imageSet) {
        const mockup = imageSet.find(i => i.type === 'mockup');
        const preview = imageSet.find(i => i.type === 'preview');
        if (mockup?.url) {
          if (this.hasMockupSlideTarget) {
            this.updateGalleryImage(this.mockupSlideTarget, mockup.url);
          }
          if (this.hasMockupThumbTarget) {
            this.updateGalleryImage(this.mockupThumbTarget, mockup.url);
          }
        }
        if (preview?.url) {
          if (this.hasPreviewSlideTarget) {
            this.updateGalleryImage(this.previewSlideTarget, preview.url);
          }
          if (this.hasPreviewThumbTarget) {
            this.updateGalleryImage(this.previewThumbTarget, preview.url);
          }
        }
      }
    }

    const colorAxis = this.optionsValue.hierarchy.find(h => h.input_style === 'swatch');
    if (colorAxis && event.target.dataset.column === colorAxis.column) {
      if (this.hasColorLabelTarget) {
        const colorId = parseInt(event.target.value, 10);
        const color = colors[colorId];
        if (color) this.colorLabelTarget.textContent = color.name;
      }
      this.updateColorSwatchActiveState();
    }

    this.updateAvailableOptions(selections, { uncheck: true });
  }

  // The gallery slide renders a <picture> with a webp <source srcset> alongside the
  // <img src>; browsers that support webp use the <source>, so setting img.src alone
  // has no visible effect. Replace the whole <img> so the old bitmap is cleared
  // immediately and the new one loads into the slide's background.
  updateGalleryImage(slideTarget, url) {
    const picture = slideTarget.querySelector('picture');
    if (!picture) return;

    picture.querySelectorAll('source').forEach(source => source.remove());

    const newImg = new Image();
    newImg.src = url;

    const oldImg = picture.querySelector('img');
    if (oldImg) {
      newImg.className = oldImg.className;
      oldImg.replaceWith(newImg);
    } else {
      picture.appendChild(newImg);
    }
  }

  updateColorSwatchActiveState() {
    this.colorSwatchTargets.forEach((wrap) => {
      const input = wrap.querySelector('input');
      wrap.classList.toggle('active', !!input?.checked);
    });
  }

  updatePrice(product) {
    if (!product) return;

    const { retail_price_formatted: regular, sale_price_formatted: sale } = Utilities.productPrices(
      product, this.sanitizedProducts,
    );
    if (this.hasPriceRegularTarget && regular) this.priceRegularTarget.textContent = this.trimCents(regular);
    if (this.hasPriceSaleTarget && sale) this.priceSaleTarget.textContent = this.trimCents(sale);
  }

  trimCents(price) {
    return price.replace(/\.00$/, '');
  }

  get hierarchyForValidation() {
    return this.optionsValue.hierarchy.map(axis => ({ slug: axis.slug, name: axis.label }));
  }

  addToCart(event) {
    const selections = this.bySlug(this.collectSelections());
    if (!Utilities.validateAddToCart(selections, this.hierarchyForValidation)) return;

    const button = event.currentTarget;
    button.disabled = true;

    const quantity = parseInt(this.hasQuantityInputTarget ? this.quantityInputTarget.value : 1, 10);

    Fetchers.fetchJSON('/add_to_cart', 'POST', JSON.stringify({
      product_id: this.productIdValue,
      design_id:  this.designIdValue,
      quantity,
      store_id:   null,
      campaign_id: null,
    }))
      .then(res => res.json())
      .then((data) => {
        if (!data.success) throw new Error('Add to cart failed');
        this.updateBadgeNotification(quantity);
        if (this.hasContainersDrawerComponentOutlet) this.containersDrawerComponentOutlet.open();
        if (this.hasNavigationCartOutlet) {
          this.navigationCartOutlet.fireRequestValue = true;
          this.navigationCartOutlet.lazyloadCartTray();
        }
        if (this.ownDrawer) this.ownDrawer.close();
      })
      .catch(() => { alert('This product is no longer available.'); })
      .finally(() => { button.disabled = false; });
  }

  collectSelections() {
    const selections = {};
    this.optionInputTargets.forEach(input => {
      if (input.checked && !input.disabled) selections[input.dataset.column] = parseInt(input.value, 10);
    });
    return selections;
  }

  // Inputs are named by configuration column (e.g. "color_id"); attrIdsOrder/Utilities
  // operate on the PDP's attribute slug (e.g. "color") — translate between the two.
  get slugByColumn() {
    if (!this.slugByColumnCache) {
      this.slugByColumnCache = Object.fromEntries(
        this.optionsValue.hierarchy.map(axis => [axis.column, axis.slug]),
      );
    }
    return this.slugByColumnCache;
  }

  bySlug(selections) {
    const bySlug = {};
    Object.entries(selections).forEach(([column, value]) => {
      bySlug[this.slugByColumn[column]] = value;
    });
    return bySlug;
  }

  findProduct(selections) {
    return Utilities.findProductFromSelections(
      this.sanitizedProducts, this.bySlug(selections), this.productOptionsShape,
    );
  }

  // Utilities.productAttributes decodes a product's compact attrIdsOrder array each call;
  // cache the decode per product (identity-stable across sanitizedProducts) so repeated
  // selection changes don't re-decode every product on every axis every time.
  decodedAttributes(product) {
    if (!this.decodedAttributesCache) this.decodedAttributesCache = new Map();
    if (!this.decodedAttributesCache.has(product)) {
      this.decodedAttributesCache.set(
        product, Utilities.productAttributes(product, this.sanitizedProducts),
      );
    }
    return this.decodedAttributesCache.get(product);
  }

  findOptionWrap(input) {
    return input.closest('.m-cart-config__color-wrap2') || input.closest('label');
  }

  // Options unreachable given the rest of the current selection are hidden entirely
  // (matching the PDP, which only ever renders currently-valid options), not just disabled.
  updateAvailableOptions(selections = this.collectSelections(), { uncheck = false } = {}) {
    const { hierarchy } = this.optionsValue;
    const inputs = this.optionInputTargets;

    hierarchy.forEach((axis) => {
      const partial = { ...selections };
      delete partial[axis.column];

      const reachableProducts = Utilities.findProductsFromSelections(
        this.sanitizedProducts, this.bySlug(partial), this.productOptionsShape,
      );
      const reachable = new Set(
        reachableProducts.productData
          .map(product => Number(this.decodedAttributes(product)[axis.slug]))
          .filter(v => !Number.isNaN(v)),
      );

      inputs.forEach((input) => {
        if (input.dataset.column !== axis.column) return;

        const isAvailable = reachable.has(parseInt(input.value, 10));
        input.disabled = !isAvailable;
        if (!isAvailable && uncheck) input.checked = false;
        const wrap = this.findOptionWrap(input);
        if (wrap) wrap.hidden = !isAvailable;
      });
    });
  }

  updateBadgeNotification(count) {
    document.querySelectorAll('.jsNotificationBadge').forEach(badge => {
      const notification = badge.notification;
      if (!notification) return;
      notification.countValue = (parseInt(notification.countValue) || 0) + count;
      notification.updateCount();
    });
  }
}
